/**
 * Product-list carousel: move arrows beside VIEW ALL and scroll the track.
 */
function getProductListSlideshow(section) {
  return (
    section.querySelector('.resource-list.resource-list__carousel slideshow-component') ||
    section.querySelector('slideshow-component.resource-list__carousel') ||
    section.querySelector('.resource-list slideshow-component')
  );
}

function getScroller(slideshow) {
  return (
    slideshow?.querySelector?.('slideshow-slides[ref="scroller"]') ||
    slideshow?.querySelector?.('slideshow-slides') ||
    null
  );
}

function getScrollStep(scroller) {
  const slide = scroller.querySelector('slideshow-slide');
  if (slide) {
    const style = getComputedStyle(scroller);
    const gap = parseFloat(style.columnGap || style.gap) || 16;
    return slide.getBoundingClientRect().width + gap;
  }
  return Math.max(scroller.clientWidth * 0.8, 280);
}

function syncArrowState(scroller, prev, next) {
  if (!scroller) return;
  const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  const left = scroller.scrollLeft;

  if (prev) {
    const atStart = left <= 2;
    prev.disabled = atStart;
    prev.toggleAttribute('disabled', atStart);
  }
  if (next) {
    const atEnd = left >= maxScroll - 2;
    next.disabled = atEnd;
    next.toggleAttribute('disabled', atEnd);
  }
}

function scrollCarousel(scroller, direction) {
  if (!scroller) return;
  scroller.scrollBy({
    left: direction === 'next' ? getScrollStep(scroller) : -getScrollStep(scroller),
    behavior: 'smooth',
  });
}

function wireArrowButton(button, scroller, direction, siblings) {
  if (!button || button.dataset.julboWired === 'true') return;
  button.dataset.julboWired = 'true';
  button.removeAttribute('on:click');

  button.addEventListener(
    'click',
    (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      if (direction === 'previous' && scroller.scrollLeft <= 2) return;
      if (direction === 'next' && scroller.scrollLeft >= maxScroll - 2) return;
      button.disabled = false;
      scrollCarousel(scroller, direction);
      requestAnimationFrame(() => syncArrowState(scroller, siblings.prev, siblings.next));
      setTimeout(() => syncArrowState(scroller, siblings.prev, siblings.next), 400);
    },
    true
  );
}

/**
 * Place arrow slot in the same header row as VIEW ALL so CSS can align them.
 */
function alignToolbarActions(section) {
  const toolbar = section.querySelector('.julbo-pl-toolbar');
  const arrowSlot = section.querySelector('[data-julbo-pl-arrows]');
  if (!toolbar || !arrowSlot) return;

  const headerContent =
    toolbar.querySelector('.section-resource-list__header > .group-block > .group-block-content') ||
    toolbar.querySelector('.section-resource-list__header .group-block-content');

  if (!headerContent) return;

  headerContent.classList.add('julbo-pl-header-content');

  if (arrowSlot.parentElement !== headerContent) {
    headerContent.appendChild(arrowSlot);
  }
}

function placeProductListArrows(section) {
  const slot = section.querySelector('[data-julbo-pl-arrows]');
  if (!slot) return;

  const slideshow = getProductListSlideshow(section);
  if (!slideshow) return;

  const scroller = getScroller(slideshow);
  if (!scroller) return;

  slideshow.setAttribute('in-viewport', '');

  let arrows = slot.querySelector('slideshow-arrows');
  if (!arrows) {
    arrows = slideshow.querySelector('slideshow-arrows');
    if (!arrows) return;
    slot.appendChild(arrows);
  }

  alignToolbarActions(section);

  const prev = arrows.querySelector('.slideshow-control--previous, [ref="previous"]');
  const next = arrows.querySelector('.slideshow-control--next, [ref="next"]');
  const siblings = { prev, next };

  wireArrowButton(prev, scroller, 'previous', siblings);
  wireArrowButton(next, scroller, 'next', siblings);

  if (scroller.dataset.julboScrollBound !== 'true') {
    scroller.dataset.julboScrollBound = 'true';
    scroller.addEventListener('scroll', () => syncArrowState(scroller, prev, next), {
      passive: true,
    });
  }

  syncArrowState(scroller, prev, next);
}

function initJulboProductLists() {
  document
    .querySelectorAll('[data-julbo-product-list], [data-testid="product-list"]')
    .forEach(placeProductListArrows);
}

function boot() {
  const run = () => initJulboProductLists();
  if (window.customElements?.whenDefined) {
    customElements.whenDefined('slideshow-component').then(run).catch(run);
  }
  run();
  requestAnimationFrame(run);
  setTimeout(run, 100);
  setTimeout(run, 500);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target;
  if (!(root instanceof Element)) return;
  const section = root.matches?.('[data-julbo-product-list], [data-testid="product-list"]')
    ? root
    : root.querySelector?.('[data-julbo-product-list], [data-testid="product-list"]');
  if (section) placeProductListArrows(section);
});
